import React, { useState, useRef, useEffect, useCallback } from 'react';
import Select, { components } from 'react-select';
import { PersonFill } from 'react-bootstrap-icons';
import './style.scss';
import { INDIVIDUAL_ORDERS } from '../../helpers/constants';

export default function CustomMultiSelect({
  id,
  onChange,
  selectedOptions,
  options,
  label,
  placeholder,
  showSelectAll = true,
  maxToShow = 3,
  className = '',
  containerClassName = '',
  menuPosition,
  menuPlacement = 'auto',
  findSelectedMatchUsing = 'value'
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectRef = useRef(null);

  const [selectedValues, setSelectedValues] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);

  const handleOnChange = e => {
    setSelectedValues(e);
    onChange(e);
  };

  const handleSelectAll = () => {
    if (selectedAll) {
      handleOnChange([]);
    } else {
      handleOnChange(options);
    }
    setSelectedAll(prevSelectedAll => !prevSelectedAll);
  };

  // Custom search input inside the options menu
  const CustomMenuList = useCallback(
    props => {
      return (
        <div>
          {showSelectAll && (
            <>
              <div className="select-all" onClick={handleSelectAll}>
                <input type="checkbox" checked={selectedAll} onChange={() => { }} /> <label>Select All</label>
              </div>
              <hr className="m-0" />
            </>
          )}
          <components.MenuList {...props}>{props.children}</components.MenuList>
        </div>
      );
    },
    [selectedAll, showSelectAll]
  );

  const CustomOption = useCallback(props => {
    const isIndividualOption = props.value === INDIVIDUAL_ORDERS;

    return (
      <div>
        <components.Option {...props} className={'select-option-items'}>
          <input type="checkbox" checked={props.isSelected} onChange={() => { }} />
          <label className={isIndividualOption ? 'individual-label d-flex align-items-center' : ''}>
            <span> {props.label}</span>
            {isIndividualOption && <PersonFill className="individual-icon" size={16} />}
          </label>
        </components.Option>
      </div>
    );
  }, []);

  const MoreSelectedBadge = ({ items }) => {
    const title = items.join(', ');
    const length = items.length;
    const label = `+ ${length} other${length === 1 ? '' : 's'} selected`;

    return (
      <div className="smallFont fw-bold text-dark" style={{ margin: '1px 1px', fontSize: '12px' }} title={title}>
        {label}
      </div>
    );
  };

  const CustomMultiValue = useCallback(({ index, getValue, ...props }) => {
    const values = getValue();
    const overflow = values.slice(maxToShow).map(x => x.label);
    const isIndividualValue = props.data?.value === INDIVIDUAL_ORDERS;

    if (index < maxToShow) {
      return (
        <div className={isIndividualValue}>
          <components.MultiValue {...props}>
            <components.MultiValueLabel {...props}>
              {props.children}
              {isIndividualValue && <PersonFill size={14} />}
            </components.MultiValueLabel>
          </components.MultiValue>
        </div>
      );
    }
    if (index === maxToShow) {
      return <MoreSelectedBadge items={overflow} />;
    }
    return null;
  }, []);

  // Close menu when clicking outside
  // Enable multiple selection of options
  useEffect(() => {
    const handleClickOutside = event => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedValues([]);
      return;
    }
    const matchedValues = options.filter(option => {
      const matchKey = findSelectedMatchUsing === 'label' ? option.label : option.value;
      if (Array.isArray(matchKey)) {
        return matchKey.some(key => selectedOptions.includes(key));
      }
      return selectedOptions.some(selected => selected === matchKey);
    });
    setSelectedValues(matchedValues);
  }, [selectedOptions, options, findSelectedMatchUsing]);

  useEffect(() => {
    const isAllSelected = selectedValues.length === options.length;
    setSelectedAll(isAllSelected);
  }, [selectedValues]);

  return (
    <div
      className={['custom-multi-select-container', containerClassName].filter(Boolean).join(' ')}
      ref={selectRef}
    >
      {label && <label className="font-weight-bold">{label}</label>}
      <Select
        id={id}
        required
        isMulti
        components={{ Option: CustomOption, MenuList: CustomMenuList, MultiValue: CustomMultiValue }}
        hideSelectedOptions={false}
        menuIsOpen={isMenuOpen}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        className={`custom-multi-select w-100 ${className}`}
        classNamePrefix="select"
        onChange={handleOnChange}
        placeholder={placeholder}
        options={options}
        value={selectedValues}
        closeMenuOnSelect={false}
        menuPlacement={menuPlacement || 'auto'}
        {...(menuPosition && {
          menuPosition: menuPosition || 'fixed',
          menuPortalTarget: document.body,
          styles: {
            menuPortal: base => ({
              ...base,
              zIndex: 99999999
            })
          }
        })}
      />
    </div>
  );
}
